
/**
 * Pads the string with zeros on the left to meet
 * either length 2 or the provided length.
 *
 * If a string that is over length of 2 or the provided length,
 * it will be trimmed.
 *
 * @param {string} str
 * @param {number} [len]
 * @returns
 */
export function padZero(str: string, len = 2): string {
    const zeros = new Array(len).join("0");
    return (zeros + str).slice(-len);
}


/**
 * Inverts a HEX color.
 *
 * Provides a color that is on the opposite side of the color wheel.
 *
 * When passed true as the second parameter, it will invert the color
 * and provide it within grey scale to help with contrast and readability.
 *
 * @source https://stackoverflow.com/a/35970186
 * @export
 * @param {string} hex
 * @param {boolean} [bw]
 * @returns
 */
export function invertColor(hex: string, bw?: boolean): string | undefined {
    if (!hex) {
        return undefined;
    }

    if (hex.indexOf("#") === 0) {
        hex = hex.slice(1);
    }
    // convert 3-digit hex to 6-digits.
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    // Nothing, not a throw. This is called during render with colours that came
    // off a remote config with no schema -- a named CSS colour, an rgb() string,
    // a typo -- and this app has no error boundary, so throwing here does not
    // degrade a header, it blanks the whole page including the form. The
    // caller's contract has always been `string | undefined`; the throw was the
    // one path that did not honour it.
    if (!/^[0-9a-f]{6}$/i.test(hex)) {
        return undefined;
    }
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    if (bw) {
        // http://stackoverflow.com/a/3943023/112731
        return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? "#000000" : "#FFFFFF";
    }
    // pad each with zeros and return
    return "#" + padZero((255 - r).toString(16)) + padZero((255 - g).toString(16)) + padZero((255 - b).toString(16));
}

/**
 * Whether two colour strings name the same colour.
 *
 * `===` is the wrong question: `#1b3a6b` and `#1B3A6B` are one colour written
 * two ways, as are `#fff` and `#ffffff`. Both callers use this to ask "is this
 * text colour indistinguishable from what it sits on?", and answering
 * "different" there skips the inversion and renders invisible text on a
 * matching background -- the exact case the guard exists to catch.
 *
 * Anything that is not a hex colour is compared as written. There is nothing to
 * canonicalise, and these values arrive from a remote config with no schema, so
 * this must answer rather than throw.
 */
export function sameColor(a?: string, b?: string): boolean {
    if (!a || !b) {
        return false;
    }

    return canonical(a) === canonical(b);
}

function canonical(color: string): string {
    const hex = color.trim().replace(/^#/, "").toLowerCase();

    if (/^[0-9a-f]{3}$/.test(hex)) {
        return hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    return /^[0-9a-f]{6}$/.test(hex) ? hex : color.trim().toLowerCase();
}
